/** Aggregate billing metrics from invoice rows (shared by Analytics + Billing Summary). */
export function metricsFromInvoices(invs) {
  const list = invs || [];
  const billing = list.reduce((s, inv) => s + (inv.total || 0), 0);
  const collected = list.reduce((s, inv) => s + (inv.paid || 0), 0);
  const govtFees = list.reduce(
    (s, inv) => s + (inv.lineItems || []).filter(l => l.type === 'govt').reduce((ss, l) => ss + (l.unitPrice || 0) * (l.qty || 1), 0),
    0,
  );
  const gst = list.reduce(
    (s, inv) => s + (inv.total || 0) - (inv.lineItems || []).reduce((ss, l) => ss + (l.unitPrice || 0) * (l.qty || 1), 0),
    0,
  );
  const dsc = list.reduce(
    (s, inv) => s + (inv.lineItems || []).filter(l => l.type === 'dsc').reduce((ss, l) => ss + (l.unitPrice || 0) * (l.qty || 1), 0),
    0,
  );
  return { billing, collected, govtFees, gst, dsc, gross: billing - govtFees - gst, count: list.length };
}

export function buildMonthlySeries(invoices, year, months) {
  return months.map((month, idx) => {
    const invs = (invoices || []).filter(inv => {
      const d = new Date(inv.date || 0);
      return d.getMonth() === idx && d.getFullYear() === year;
    });
    return { key: month, label: month, ...metricsFromInvoices(invs) };
  });
}

/** Per-client billing rollup for Billing Summary table. */
export function buildClientBillingTable(invoices, clients = []) {
  const byClient = {};
  for (const inv of (invoices || []).filter(i => i.status !== 'void')) {
    const cid = inv.clientId;
    if (!cid) continue;
    if (!byClient[cid]) {
      byClient[cid] = {
        clientId: cid,
        clientName: inv.clientName || clients.find(c => c.id === cid)?.name || cid,
        billed: 0,
        collected: 0,
        count: 0,
      };
    }
    byClient[cid].billed += Number(inv.total) || 0;
    byClient[cid].collected += Number(inv.paid) || 0;
    byClient[cid].count += 1;
  }
  return Object.values(byClient)
    .map(row => ({ ...row, outstanding: Math.max(0, row.billed - row.collected) }))
    .sort((a, b) => b.billed - a.billed);
}
export function buildWeeklySeries(invoices, weekCount = 12, now = new Date()) {
  const out = [];
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  for (let w = weekCount - 1; w >= 0; w--) {
    const weekEnd = new Date(endOfToday);
    weekEnd.setDate(weekEnd.getDate() - w * 7);
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() - 6);
    weekStart.setHours(0, 0, 0, 0);
    const invs = (invoices || []).filter(inv => {
      const d = new Date(inv.date || 0);
      return d >= weekStart && d <= weekEnd;
    });
    const label = weekStart.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    out.push({ key: `w${w}`, label, weekStart, weekEnd, ...metricsFromInvoices(invs) });
  }
  return out;
}
